import { TRADE_SPECIFIC_CELL_KEYS, TRADE_SPECIFIC_QUESTION_KEYS } from './questionConstants';
import {
  getQuestionByKey,
  getUserResponse,
  didUserSelectOptionWithAnswerByKey,
  didUserSelectAnyOptionWithAnswersByKey,
} from './scoreHelpers';
import { CareerUserResponseSet, CareerQuestion } from '../models/models';

export function determineIfTradeRecommended(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  if (!responseSet || !questions) {
    return false;
  }

  const cell_L5 = determineIfTradeRecommended_Cell_L5(responseSet, questions);
  const cell_M5 = determineIfTradeRecommended_Cell_M5(responseSet, questions);

  return cell_L5 || cell_M5;
}

/** Calculates Cell L5 function */
function determineIfTradeRecommended_Cell_L5(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  const enjoyActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_ENJOY,
  );
  const dislikeActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE,
  );
  const educationalChoicesQuestionHghSchool = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.EDUCATIONAL_CHOICES,
  );

  // here we're just looking to confirm that the questions exist, not necessarily that the user has answered them
  if (
    !enjoyActivitiesQuestion ||
    !dislikeActivitiesQuestion ||
    !educationalChoicesQuestionHghSchool
  ) {
    return false;
  }

  // Get user responses
  const enjoyActivitiesResponse = getUserResponse(responseSet, enjoyActivitiesQuestion.id);
  const dislikeActivitiesResponse = getUserResponse(responseSet, dislikeActivitiesQuestion.id);
  const educationalChoicesHighSchoolResponse = getUserResponse(
    responseSet,
    educationalChoicesQuestionHghSchool.id,
  );

  if (
    !enjoyActivitiesResponse ||
    !dislikeActivitiesResponse ||
    !educationalChoicesHighSchoolResponse
  ) {
    return false;
  }

  // Check if the user enjoys any of the specified activities
  const targetActivities = [
    TRADE_SPECIFIC_CELL_KEYS.cell_D53,
    TRADE_SPECIFIC_CELL_KEYS.cell_D56,
    TRADE_SPECIFIC_CELL_KEYS.cell_D61,
  ];

  const enjoysAny = didUserSelectAnyOptionWithAnswersByKey({
    response: enjoyActivitiesResponse,
    question: enjoyActivitiesQuestion,
    answerTexts: targetActivities,
  });

  // Check if the user dislikes any of the specified activities
  const dislikesAny = didUserSelectAnyOptionWithAnswersByKey({
    response: dislikeActivitiesResponse,
    question: dislikeActivitiesQuestion,
    answerTexts: targetActivities,
  });

  // Check if the user chose "A very selective college"
  // we only check against high school, since very selective college is not an option for a student in college
  const choseVerySelectiveCollege = didUserSelectOptionWithAnswerByKey({
    response: educationalChoicesHighSchoolResponse,
    question: educationalChoicesQuestionHghSchool,
    answerText: TRADE_SPECIFIC_CELL_KEYS.cell_D77,
  });

  // Final decision logic
  return enjoysAny && !dislikesAny && !choseVerySelectiveCollege;
}

/** Calculates Cell M5 function */
function determineIfTradeRecommended_Cell_M5(
  responseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  // Find questions
  const dislikeActivitiesQuestion = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.ACTIVITIES_YOU_DONT_LIKE,
  );
  const educationalChoicesQuestionHighSchool = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.EDUCATIONAL_CHOICES,
  );
  const educationalChoicesQuestionCollege = getQuestionByKey(
    questions,
    TRADE_SPECIFIC_QUESTION_KEYS.EDUCATIONAL_CHOICES_COLLEGE,
  );

  // here we're just looking to confirm that the questions exist, not necessarily that the user has answered them
  if (
    !dislikeActivitiesQuestion ||
    !educationalChoicesQuestionHighSchool ||
    !educationalChoicesQuestionCollege
  ) {
    return false;
  }

  /** DISLIKE ACTIVITIES QUESTION  */
  // Get user responses for dislikes
  const dislikeActivitiesResponse = getUserResponse(responseSet, dislikeActivitiesQuestion.id);

  if (!dislikeActivitiesResponse) {
    return false;
  }

  const targetDislikeActivities = [
    TRADE_SPECIFIC_CELL_KEYS.cell_D65,
    TRADE_SPECIFIC_CELL_KEYS.cell_D68,
    TRADE_SPECIFIC_CELL_KEYS.cell_D73,
  ];

  // Check if the user dislikes any of the specified activities
  const dislikesAny = didUserSelectAnyOptionWithAnswersByKey({
    response: dislikeActivitiesResponse,
    question: dislikeActivitiesQuestion,
    answerTexts: targetDislikeActivities,
  });

  /** EDUCATIONAL CHOICES QUESTION */

  // Get user responses for educational choices, for either high school or college
  const educationalChoicesHighSchoolResponse = getUserResponse(
    responseSet,
    educationalChoicesQuestionHighSchool.id,
  );

  const educationalChoicesCollegeResponse = getUserResponse(
    responseSet,
    educationalChoicesQuestionCollege.id,
  );

  // Check if the user chose "Trade school or apprenticeship program" in either high school or college edu level
  const choseTradeSchoolHighSchool =
    educationalChoicesHighSchoolResponse &&
    didUserSelectOptionWithAnswerByKey({
      response: educationalChoicesHighSchoolResponse,
      question: educationalChoicesQuestionHighSchool,
      answerText: TRADE_SPECIFIC_CELL_KEYS.cell_D78,
    });

  const choseTradeSchoolCollege =
    educationalChoicesCollegeResponse &&
    didUserSelectOptionWithAnswerByKey({
      response: educationalChoicesCollegeResponse,
      question: educationalChoicesQuestionCollege,
      answerText: TRADE_SPECIFIC_CELL_KEYS.cell_D78a,
    });

  const didChooseTradeSchool = !!(choseTradeSchoolHighSchool || choseTradeSchoolCollege);

  /** FINAL DECISION LOGIC
   *
   * the user must have chosen trade school or apprenticeship program, and not dislike any of the specified activities
   */
  return didChooseTradeSchool && !dislikesAny;
}
